import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  BookOpen, 
  MessageSquare, 
  User, 
  BarChart3, 
  Users, 
  CreditCard, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell, // Import Bell icon for notifications
  Download, // Import Download icon
  School,
  GraduationCap,
  Clock, // Sprint traçabilité apprenants (01/10/2026) — journal des connexions
  Timer,  // Sprint traçabilité apprenants (01/10/2026) — temps passé par formation
  ClipboardCheck // Sprint émargement (09/10/2026) — feuilles d'émargement
} from 'lucide-react';
import { api, API_BASE_URL } from '../api';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: string;
  showBadge?: boolean;
  isDownload?: boolean;
  downloadName?: string;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const fetchUnreadMessagesCount = useCallback(async () => {
    if (!user || !localStorage.getItem('token')) return;
    try {
      const response = await api.get<{ unreadCount: number }>('/messages/unread/count');
      setUnreadMessageCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching unread message count:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadMessagesCount();

    const pollingInterval = setInterval(() => {
      fetchUnreadMessagesCount();
    }, 300000); // Poll every 5 minutes

    return () => clearInterval(pollingInterval);
  }, [fetchUnreadMessagesCount]);

  const downloadLinks = {
    android: `${API_BASE_URL}/api/downloads/android`,
  };

  const getNavigationItems = (): NavItem[] => {
    if (!user?.role) return [];

    const baseItems = [
      { name: 'Dashboard', href: `/${user.role}/dashboard`, icon: 'Home' },
    ];

    if (user.role === 'student') {
      return [
        ...baseItems,
        { name: 'Mes Cours', href: '/student/courses', icon: 'BookOpen' },
        { name: 'Quiz & Tests', href: '/student/quizzes', icon: 'BarChart3' },
        { name: 'Statistiques', href: '/student/stats', icon: 'BarChart3' },
        { name: 'Ma Classe', href: '/student/class', icon: 'GraduationCap' },
        { name: 'Frais & Paiements', href: '/student/payments', icon: 'CreditCard' },
        // Sprint « émargement » (09/10/2026)
        { name: 'Mon émargement', href: '/student/attendance', icon: 'ClipboardCheck' },
        { name: 'Messages', href: '/student/messages', icon: 'MessageSquare', showBadge: true },
        { name: 'Profil', href: '/student/profile', icon: 'User' },
        { name: 'App Android', href: downloadLinks.android, icon: 'Download', isDownload: true, downloadName: 'lms-kayliss-android.apk' },
      ];
    } else if (user.role === 'professor') {
      return [
        ...baseItems,
        { name: 'Mes Cours', href: '/professor/courses', icon: 'BookOpen' },
        { name: 'Classes', href: '/professor/classes', icon: 'School' },
        // Sprint « émargement » (09/10/2026)
        { name: 'Émargements', href: '/professor/attendance', icon: 'ClipboardCheck' },
        { name: 'Quiz & Tests', href: '/professor/quizzes', icon: 'BarChart3' },
        { name: 'Messages', href: '/professor/messages', icon: 'MessageSquare', showBadge: true },
        { name: 'Profil', href: '/professor/profile', icon: 'User' },
        { name: 'App Android', href: downloadLinks.android, icon: 'Download', isDownload: true, downloadName: 'lms-kayliss-android.apk' },
      ];
    } else if (user.role === 'admin') {
      return [
        ...baseItems,
        { name: 'Utilisateurs', href: '/admin/users', icon: 'Users' },
        { name: 'Catégories', href: '/admin/category', icon: 'BookOpen' },
        { name: 'Cours', href: '/admin/courses', icon: 'BookOpen' },
        { name: 'Classes', href: '/admin/classes', icon: 'School' },
        { name: 'Quiz & Tests', href: '/admin/quizzes', icon: 'BarChart3' },
        { name: 'Paiements', href: '/admin/payments', icon: 'CreditCard' },
        { name: 'Statistiques', href: '/admin/stats', icon: 'BarChart3' },
        // Sprint « traçabilité apprenants » (01/10/2026)
        { name: 'Connexions', href: '/admin/connections', icon: 'Clock' },
        { name: 'Temps par formation', href: '/admin/time-spent', icon: 'Timer' },
        // Sprint « émargement » (09/10/2026)
        { name: 'Émargements', href: '/admin/attendance', icon: 'ClipboardCheck' },
        { name: 'Messages', href: '/admin/messages', icon: 'MessageSquare', showBadge: true },
        { name: 'App Android', href: downloadLinks.android, icon: 'Download', isDownload: true, downloadName: 'lms-kayliss-android.apk' },
      ];
    }
    return baseItems;
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Home': return Home;
      case 'BookOpen': return BookOpen;
      case 'MessageSquare': return MessageSquare;
      case 'User': return User;
      case 'BarChart3': return BarChart3;
      case 'Users': return Users;
      case 'CreditCard': return CreditCard;
      case 'Settings': return Settings;
      case 'Download': return Download;
      case 'School': return School;
      case 'GraduationCap': return GraduationCap;
      // Sprint « traçabilité apprenants » (01/10/2026)
      case 'Clock': return Clock;
      case 'Timer': return Timer;
      // Sprint « émargement » (09/10/2026)
      case 'ClipboardCheck': return ClipboardCheck;
      default: return Home;
    }
  };

  const navigationItems = getNavigationItems();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between h-20 px-4 border-b border-gray-200 bg-brand-600">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/70 bg-white">
              <img
                src="/logo.png"
                alt="Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-bold text-lg text-white truncate">LMS Kayliss</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/90 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-5 px-2">
          <div className="space-y-1">
            {navigationItems.map((item) => {
              const Icon = getIconComponent(item.icon);
              const isDownloadLink = Boolean(item.isDownload);
              const isActive = !isDownloadLink && location.pathname === item.href;
              const sharedClasses = `
                group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150
                ${isActive 
                  ? 'bg-brand-100 text-black-900' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
              `;

              const content = (
                <>
                  <Icon 
                    className={`
                      mr-3 h-5 w-5 transition-colors duration-150
                      ${isActive ? 'text-brand-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `}
                  />
                  {item.name}
                  {item.showBadge && unreadMessageCount > 0 && (
                    <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-brand-500 text-white rounded-full">
                      {unreadMessageCount}
                    </span>
                  )}
                </>
              );

              if (isDownloadLink) {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className={sharedClasses}
                    onClick={() => setSidebarOpen(false)}
                    download={item.downloadName}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {content}
                  </a>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={sharedClasses}
                  onClick={() => setSidebarOpen(false)}
                >
                  {content}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="absolute bottom-0 w-full p-4">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
          >
            <LogOut className="mr-3 h-5 w-5 text-gray-400" />
            Déconnexion
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="bg-yellow shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="text-gray-500">Bonjour </span>
                <span className="font-medium text-gray-900 ml-1">{user?.name}</span>
              </div>
              <div className="w-8 h-8 rounded-full bg-brand-500 text-yellow flex items-center justify-center">
                <span className="font-medium text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <div className="container mx-auto px-6 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
